"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import type { CSSProperties } from "react";
import Image from "next/image"; // Perf: tutte le immagini hero migrate a next/image
import type { EventoDto, ApprofondimentoDto } from "@/lib/api";
import { resolveMediaUrl, resolveImageUrl, isImageMedia } from "@/lib/media-url";
import { getCatColors } from "@/lib/category-colors";
import { toYouTubeEmbed, youtubeThumbnail } from "@/lib/youtube";
import YouTubeFacade from "@/components/YouTubeFacade";
import {
  isScreenshotMedia,
  shouldUseHeroImage,
  subscribeHeroImagePreference,
} from "@/lib/hero-image-preference";

export interface HeroSlideMedia {
  type: "video" | "pdf" | "podcast" | "gallery" | "slide" | "document" | "quote";
  /** URL principale (video, pdf, podcast, slide file, document file). Opzionale per quote/gallery. */
  url?: string;
  title?: string;
  /** Gallery: array di URL immagini da mostrare in mini-carousel */
  images?: string[];
  /** Quote: testo citazione */
  quote?: string;
  /** Quote: autore/fonte */
  quoteAuthor?: string;
  /** Slide/document: nome file per UI */
  filename?: string;
}

export interface HeroSlide {
  type: "apertura" | "evento" | "approfondimento";
  title: string;
  subtitle?: string;
  categorySlug?: string;
  excerpt?: string;
  source?: string;
  authors?: string[];
  quote?: string;
  quoteAuthor?: string;
  imageUrl?: string;
  /** True se `imageUrl` è lo screenshot automatico e non un'immagine caricata. */
  imageIsScreenshot?: boolean;
  /** Id dell'approfondimento sottostante (serve per la preferenza hero). */
  approfondimentoId?: number;
  href?: string;
  cta?: string;
  badge?: string;
  /** Embedded media for apertura slide */
  media?: HeroSlideMedia;
}

function toPlainText(input?: string | null): string {
  if (!input) return "";
  return input
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

function toExcerpt(input?: string | null, maxLen = 320): string | undefined {
  const text = toPlainText(input);
  if (!text) return undefined;
  if (text.length <= maxLen) return text;
  return `${text.slice(0, maxLen).trim()}…`;
}

function buildSlides(
  eventi: EventoDto[],
  approfondimenti: ApprofondimentoDto[],
): HeroSlide[] {
  const slides: HeroSlide[] = [];

  // Slide 1 — apertura
  slides.push({
    type: "apertura",
    title: "Una fede che\ndiventa cultura",
    subtitle: "Centro Culturale Blaise Pascal — Reggio Emilia, dal 1986",
    href: "/chi-siamo",
    cta: "Scopri chi siamo",
  });

  // Slide 2-3 — ultimi 2 eventi
  eventi.slice(0, 2).forEach((ev, idx) => {
    const img = isImageMedia(ev.foto) ? ev.foto : isImageMedia(ev.volantino) ? ev.volantino : null;
    const d = new Date(ev.data);
    const dateStr = d.toLocaleDateString("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    // Stessa logica di HeroCarouselBlock.eventoToSlide per il video
    const videoUrlItem = ev.mediaItems?.find((m) => m.mediaType === "VIDEO_URL" && m.externalUrl);
    const videoFileItem = ev.mediaItems?.find((m) => m.mediaType === "VIDEO_FILE" && m.media);
    let media: HeroSlideMedia | undefined;
    if (videoUrlItem?.externalUrl) {
      media = { type: "video", url: videoUrlItem.externalUrl, title: videoUrlItem.title ?? ev.titolo };
    } else if (videoFileItem?.media) {
      const url = resolveMediaUrl(videoFileItem.media);
      if (url) media = { type: "video", url, title: videoFileItem.title ?? ev.titolo };
    } else if (ev.videoUrl) {
      media = { type: "video", url: ev.videoUrl, title: ev.titolo };
    }
    // Fallback immagine per lo sfondo "sfumato": se l'evento non ha foto né
    // volantino come immagine, ma c'è un video YouTube, usa la sua thumbnail.
    // Così lo slide non resta con sfondo blu piatto.
    const primaryImage = resolveMediaUrl(img) ?? undefined;
    const youtubeUrl = videoUrlItem?.externalUrl ?? (ev.videoUrl ?? undefined);
    const fallbackImage = !primaryImage && youtubeUrl ? (youtubeThumbnail(youtubeUrl, "maxres") ?? youtubeThumbnail(youtubeUrl, "hq") ?? undefined) : undefined;
    slides.push({
      type: "evento",
      title: ev.titolo,
      subtitle: `${dateStr}${ev.luogo ? " — " + ev.luogo : ""}`,
      imageUrl: primaryImage ?? fallbackImage,
      href: `/eventi/${ev.slug}`,
      cta: "Scopri l'evento",
      badge: ev.daNonPerdere ? "In evidenza" : d >= new Date() ? "Prossimo evento" : "Evento recente",
      media,
    });
  });

  // Approfondimenti: raggruppa per categoria (esclusa "approfondimenti"),
  // poi interleave: ultimo di ogni categoria, poi penultimo, poi terzultimo
  const catMap = new Map<string, ApprofondimentoDto[]>();
  const catOrder: string[] = [];

  for (const a of approfondimenti) {
    const cat = a.categories?.find((c) => c.slug !== "approfondimenti") ?? a.categories?.[0];
    const key = cat?.slug || "__none__";
    if (!catMap.has(key)) {
      catMap.set(key, []);
      catOrder.push(key);
    }
    catMap.get(key)!.push(a);
  }

  // 3 round: ultimo, penultimo, terzultimo di ogni categoria
  for (let round = 0; round < 3; round++) {
    for (const catSlug of catOrder) {
      const items = catMap.get(catSlug)!;
      if (round >= items.length) continue;
      const a = items[round];
      const cat = a.categories?.find((c) => c.slug !== "approfondimenti") ?? a.categories?.[0];
      slides.push({
        type: "approfondimento",
        title: a.titolo,
        subtitle: cat?.slug !== "approfondimenti" ? cat?.name : undefined,
        categorySlug: cat?.slug !== "approfondimenti" ? cat?.slug : undefined,
        excerpt: toExcerpt(a.contenuto),
        source: a.fonte || undefined,
        authors: a.autori,
        // heroImage e' il campo derivato dal backend che risolve gia' la
        // preferenza editoriale (AUTO=immagine??screenshot, USE_SCREENSHOT, HIDE);
        // l'ordine fallback (heroImage ?? immagine ?? screenshot) copre anche i
        // DTO legacy cached prima dell'introduzione di heroImage.
        imageUrl: resolveImageUrl(a.heroImage ?? a.immagine ?? a.screenshot) ?? undefined,
        imageIsScreenshot: isScreenshotMedia(a.heroImage ?? a.immagine ?? a.screenshot) || (!a.immagine && !!a.screenshot),
        approfondimentoId: a.id,
        href: `/approfondimenti/${a.slug}`,
        cta: "Leggi",
        badge: (cat?.slug !== "approfondimenti" ? cat?.name : undefined) || "Approfondimento",
      });
    }
  }

  return slides;
}

const AUTOPLAY_MS = 6000;


/**
 * Perf-FIX (Lighthouse "Riduci il codice JavaScript inutilizzato"):
 * il YouTubeFacade definito qui inizialmente e' stato estratto in
 * src/components/YouTubeFacade.tsx per riuso anche da PageRenderer
 * (block "youtubeGallery" e block "video"). Storia + razionale completo
 * nel file di destinazione.
 */

/** Render embedded media for apertura/evento slide */
function HeroEmbedMedia({ media }: { media: HeroSlideMedia }) {
  if (media.type === "video") {
    const url = media.url;
    if (!url) return null;
    const embedUrl = toYouTubeEmbed(url);
    if (embedUrl) {
      return (
        // "relative" obbligatorio: YouTubeFacade usa absolute inset-0 sia
        // sul bottone facade che sull'iframe.
        <div className="relative w-full max-w-lg mx-auto aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20 pointer-events-auto bg-black">
          <YouTubeFacade
            embedUrl={embedUrl}
            thumbnailSourceUrl={url}
            title={media.title}
            thumbnailSizes="(min-width: 1024px) 512px, 100vw"
          />
        </div>
      );
    }
    // Generic video URL
    return (
      <div className="w-full max-w-lg mx-auto aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20 pointer-events-auto">
        <video src={url} controls className="w-full h-full object-cover" />
      </div>
    );
  }

  if (media.type === "pdf" || media.type === "slide" || media.type === "document") {
    if (!media.url) return null;
    const accent = media.type === "pdf" ? "bg-red-500/20" : media.type === "slide" ? "bg-violet-500/20" : "bg-neutral-500/20";
    const icon = media.type === "pdf" ? "📄" : media.type === "slide" ? "📊" : "📎";
    const defaultTitle = media.type === "pdf" ? "Documento PDF" : media.type === "slide" ? "Diapositive" : "Documento";
    const hint = media.type === "slide" ? "Clicca per vedere le diapositive" : "Clicca per aprire il documento";
    return (
      <div className="w-full max-w-md mx-auto pointer-events-auto">
        <a
          href={media.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex items-center gap-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4 hover:bg-white/20 transition-colors"
        >
          <div className={`shrink-0 w-14 h-14 ${accent} rounded-lg flex items-center justify-center`}>
            <span className="text-3xl">{icon}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white font-bold text-sm truncate">{media.title || media.filename || defaultTitle}</div>
            <div className="text-white/60 text-xs mt-0.5">{hint}</div>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white/50 group-hover:text-white transition-colors shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
          </svg>
        </a>
      </div>
    );
  }

  if (media.type === "gallery") {
    const imgs = (media.images ?? []).filter(Boolean);
    if (imgs.length === 0) return null;
    // Mini-gallery: immagine principale + strip di thumb cliccabili che si aprono in nuova tab
    const [main, ...rest] = imgs;
    return (
      <div className="w-full max-w-lg mx-auto pointer-events-auto space-y-2">
        {media.title && <div className="text-white/80 text-xs font-semibold text-center">🖼 {media.title}</div>}
        <a href={main} target="_blank" rel="noopener noreferrer" className="block rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20 hover:ring-white/40 transition-all">
          {/* Perf: gallery main image, max width ~512px su lg */}
          <Image
            src={main}
            alt={media.title || ""}
            width={512}
            height={320}
            sizes="(min-width: 1024px) 512px, 100vw"
            className="w-full h-auto max-h-80 object-cover"
          />
        </a>
        {rest.length > 0 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1">
            {rest.slice(0, 6).map((src, i) => (
              <a key={i} href={src} target="_blank" rel="noopener noreferrer" className="relative flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden ring-1 ring-white/20 hover:ring-white/50 transition-all">
                {/* Perf: thumb 64x64 fissi (w-16 h-16) */}
                <Image
                  src={src}
                  alt=""
                  width={64}
                  height={64}
                  sizes="64px"
                  className="w-full h-full object-cover"
                />
              </a>
            ))}
            {rest.length > 6 && (
              <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-white/10 backdrop-blur-sm flex items-center justify-center text-white/80 text-xs font-bold">
                +{rest.length - 6}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (media.type === "quote") {
    if (!media.quote) return null;
    return (
      <div className="w-full max-w-xl mx-auto pointer-events-auto">
        <blockquote className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-5 sm:px-8 sm:py-6 shadow-2xl">
          <span className="absolute top-2 left-3 text-5xl text-white/30 leading-none font-serif" aria-hidden>&ldquo;</span>
          <p className="relative text-white text-base sm:text-lg font-medium leading-relaxed italic pl-6">
            {media.quote}
          </p>
          {(media.quoteAuthor || media.title) && (
            <footer className="mt-3 pl-6 text-right text-white/70 text-xs sm:text-sm font-semibold tracking-wide">
              — {media.quoteAuthor || media.title}
            </footer>
          )}
        </blockquote>
      </div>
    );
  }

  if (media.type === "podcast") {
    if (!media.url) return null;
    const url = media.url;
    const isSpotify = url.includes("spotify.com");
    const isAudioFile = /\.(mp3|m4a|ogg|wav|aac|flac)(\?|#|$)/i.test(url);

    // 1) Spotify → embed player ufficiale
    if (isSpotify) {
      const embedUrl = url.includes("/embed")
        ? url
        : url.replace("/episode/", "/embed/episode/").replace("/show/", "/embed/show/");
      return (
        <div className="w-full max-w-lg mx-auto pointer-events-auto">
          {media.title && <div className="text-white/80 text-xs font-semibold mb-2 text-center">🎙️ {media.title}</div>}
          <div className="rounded-xl overflow-hidden shadow-2xl ring-1 ring-white/20">
            <iframe
              src={embedUrl}
              width="100%"
              height="152"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="rounded-xl"
            />
          </div>
        </div>
      );
    }

    // 2) File audio diretto (MP3/M4A/…) → player <audio>
    if (isAudioFile) {
      return (
        <div className="w-full max-w-lg mx-auto pointer-events-auto">
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center shrink-0">
                <span className="text-2xl">🎙️</span>
              </div>
              <div className="min-w-0">
                <div className="text-white font-bold text-sm truncate">{media.title || "Podcast"}</div>
                <div className="text-white/50 text-xs">Ascolta l&apos;episodio</div>
              </div>
            </div>
            <audio src={url} controls className="w-full h-10" style={{ colorScheme: "dark" }} />
          </div>
        </div>
      );
    }

    // 3) Pagina provider senza embed (Amazon Music, Apple Podcasts, …) →
    //    card cliccabile con bottone "Ascolta su …" (Amazon non espone embed).
    const providerLabel = url.includes("music.amazon")
      ? "Amazon Music"
      : url.includes("podcasts.apple") || url.includes("apple.com")
        ? "Apple Podcasts"
        : url.includes("youtube") || url.includes("youtu.be")
          ? "YouTube"
          : "il podcast";
    return (
      <div className="w-full max-w-lg mx-auto pointer-events-auto">
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white/10 backdrop-blur-md border border-white/20 rounded-xl px-6 py-4 hover:bg-white/15 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center shrink-0">
              <span className="text-2xl">🎙️</span>
            </div>
            <div className="min-w-0">
              <div className="text-white font-bold text-sm truncate">{media.title || "Podcast"}</div>
              <div className="text-white/70 text-xs">▶ Ascolta su {providerLabel}</div>
            </div>
          </div>
        </a>
      </div>
    );
  }

  return null;
}



export function HeroCarousel({
  eventi,
  approfondimenti,
  slides: externalSlides,
  variant = "full",
}: {
  eventi?: EventoDto[];
  approfondimenti?: ApprofondimentoDto[];
  /** Se forniti, usati direttamente al posto di buildSlides() */
  slides?: HeroSlide[];
  /** "full" (default) riproduce il layout storico; "compact" usa altezza
   *  e tipografia più contenute, mantenendo la stessa struttura. */
  variant?: "full" | "compact";
}) {
  const slidesRaw = externalSlides ?? buildSlides(eventi ?? [], approfondimenti ?? []);
  const isCompact = variant === "compact";
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  // Versione reattiva alle preferenze admin: se per un approfondimento l'admin
  // ha disabilitato lo screenshot via localStorage, qui nascondiamo l'imageUrl
  // così il rendering ricade sul post-it.
  const [prefTick, setPrefTick] = useState(0);
  useEffect(() => subscribeHeroImagePreference(() => setPrefTick((t) => t + 1)), []);
  const slides = slidesRaw.map((s) => {
    if (s.type !== "approfondimento" || !s.imageUrl || !s.approfondimentoId) return s;
    const media = { filename: s.imageIsScreenshot ? "screenshot.png" : "", url: s.imageUrl } as { filename: string; url: string };
    const keep = shouldUseHeroImage(s.approfondimentoId, media as unknown as never);
    return keep ? s : { ...s, imageUrl: undefined };
  });
  // Riferimento al tick per forzare recompute quando la preferenza cambia
  void prefTick;
  const animatingRef = useRef(false);
  const currentRef = useRef(current);
  currentRef.current = current;

  const goTo = useCallback(
    (idx: number) => {
      if (animatingRef.current) return;
      animatingRef.current = true;
      setCurrent(idx);
      setTimeout(() => { animatingRef.current = false; }, 500);
    },
    [],
  );

  const next = useCallback(() => {
    goTo((currentRef.current + 1) % slides.length);
  }, [slides.length, goTo]);

  const prev = useCallback(() => {
    goTo((currentRef.current - 1 + slides.length) % slides.length);
  }, [slides.length, goTo]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, next]);

  const slide = slides[current];
  const catColors = getCatColors(slide?.categorySlug);
  const postitStyle: CSSProperties & Record<string, string> = {
    "--postit-from": catColors.postFrom,
    "--postit-to": catColors.postTo,
    "--postit-accent": catColors.accent,
  };

  return (
    <section
      className={`relative w-full ${
        isCompact
          ? "min-h-[360px] sm:min-h-[48vh] lg:min-h-[52vh]"
          : "min-h-[82vh] sm:min-h-[78vh]"
      } flex items-center justify-center overflow-hidden rounded-2xl sm:rounded-3xl mx-auto max-w-[calc(100%-1rem)] sm:max-w-[calc(100%-2rem)] mt-2 sm:mt-3 shadow-xl`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="Carosello hero"
    >
      {/* ─── SFONDI per ogni tipo di slide ─── */}
      {slides.map((s, i) => {
        if (s.type === "apertura") {
          return (
            <div
              key={`bg-${i}`}
              aria-hidden={i !== current}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === current ? "opacity-100" : "opacity-0"
              }`}
            >
              {/* Foto di apertura */}
              {/* Perf: hero apertura full-bleed → Image fill 100vw, priority=true
                  per la PRIMA slide (LCP candidate). object-top mantenuto. */}
              <Image
                src={s.imageUrl || "/hero-apertura.jpg"}
                alt=""
                fill
                sizes="100vw"
                priority={i === 0}
                // object-top: gli screenshot automatici sono ora viewport
                // mobile dell'articolo sorgente (step C12), la "parte alta"
                // (titolo/meta/lede) sta in cima → ancoriamo il crop li'.
                className="object-cover object-top"
              />
              {/* Overlay scuro per leggibilità testo */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#050d1a]/80 via-[#0f2a45]/70 to-[#1a3a5c]/60" />
              {/* Pattern sottile */}
              <div className="absolute inset-0 opacity-[0.025]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>
          );
        }
        if (s.type === "evento") {
          return (
            <div
              key={`bg-${i}`}
              aria-hidden={i !== current}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === current ? "opacity-100" : "opacity-0"
              } bg-[#050d1a]`}
            >
              {s.imageUrl && (
                <>
                  {/* Perf-FIX (Lighthouse "Migliora il caricamento delle immagini"):
                      pre-fix qui c'erano DUE <Image fill> sovrapposte (una blur-xl
                      opacity-50 atmosferica + una opacity-20 nitida per
                      riconoscibilita'). Con il loader passthrough entrambe
                      fetchavano la versione full-res del backend CMS — il
                      doppio costo per slide.
                      Soluzione: mantengo SOLO la versione nitida opacity-20
                      (quella che da' identita' visiva all'evento — si vede
                      il "fantasma" della hero image). Il blur layer era pura
                      atmosfera, rimovibile senza perdita informativa. */}
                  <Image
                    src={s.imageUrl}
                    alt=""
                    fill
                    sizes="100vw"
                    className="object-cover opacity-20"
                    priority={i === current}
                  />
                </>
              )}
              {/* Overlay gradient per garantire contrasto del testo a destra */}
              <div className="absolute inset-0 bg-gradient-to-r from-[#050d1a]/90 via-[#0f2a45]/75 to-[#050d1a]/50" />
            </div>
          );
        }
        if (s.type === "approfondimento") {
          // Ogni slide usa i propri colori
          const sBg = getCatColors(s.categorySlug);
          return (
            <div
              key={`bg-${i}`}
              aria-hidden={i !== current}
              className={`absolute inset-0 transition-opacity duration-700 ${
                i === current ? "opacity-100" : "opacity-0"
              }`}
            >
              {/* Sfondo molto scuro con leggera tinta di categoria */}
              <div
                className="absolute inset-0"
                style={{ background: `linear-gradient(145deg, ${sBg.bgFrom} 0%, ${sBg.bgTo} 100%)` }}
              />
              {/* Bagliore colorato dalla sinistra */}
              <div
                className="absolute inset-0"
                style={{ background: `radial-gradient(ellipse 65% 90% at 0% 55%, ${sBg.accentRgba} 0%, transparent 65%)` }}
              />
              {/* Perf-FIX (Lighthouse "Migliora il caricamento delle immagini"
                  — 7715 KiB risparmiabili sulla homepage):
                  pre-fix qui c'era un <Image fill blur-2xl opacity-30 mix-blend-screen>
                  che fetchava la hero image dell'approfondimento in versione
                  full-res (~600 KiB cad.) per renderla quasi invisibile sotto
                  blur estremo. Il loader custom passthrough (vedi
                  src/lib/image-loader.ts) ignora il prop sizes, quindi non
                  potevamo "rimpicciolire" lato Next.
                  Soluzione: rimosso completamente l'overlay. La slide vive
                  benissimo con il solo gradient + radial accent della categoria
                  (vedi blocchi sopra: ogni categoria ha il suo bagliore
                  colorato e gradient distintivo). L'aesthetic non perde nulla
                  di significativo perche' il blur era atmosferico, non
                  informativo. Se in futuro il backend espone
                  /api/media/{id}/thumbnail?w=128 (resize on-the-fly o cache),
                  si puo' ripristinare l'<Image> qui usando un loader smart
                  che aggiunga il query param solo per gli host CMS. */}
              {/* Punti decorativi */}
              <div className="absolute inset-0 opacity-[0.04]" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='9' y='9' width='2' height='2' fill='%23ffffff'/%3E%3C/svg%3E")`,
              }} />
            </div>
          );
        }
        return null;
      })}

      {/* ── CONTENUTO SLIDE ───────────────────── */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">

        {/* ─ APERTURA ─ */}
        {slide.type === "apertura" && (
          <div key={`apertura-${current}`} className={`relative z-10 px-6 sm:px-10 ${isCompact ? "max-w-3xl" : "max-w-4xl"} w-full animate-fade-in ${slide.media ? "flex flex-col lg:flex-row items-center gap-6 lg:gap-10" : "text-center"}`}>
            <div className={slide.media ? "flex-1 min-w-0 text-center lg:text-left" : ""}>
              <h1 className={`${isCompact ? "text-2xl sm:text-3xl lg:text-4xl mb-3" : "text-3xl sm:text-5xl lg:text-6xl mb-4"} font-extrabold text-white leading-tight drop-shadow-lg whitespace-pre-line tracking-tight`}>
                {slide.title}
              </h1>
              {slide.subtitle && (
                <p className={`text-white/70 ${isCompact ? "text-xs sm:text-sm mb-4" : "text-sm sm:text-base mb-6"} font-medium tracking-wide uppercase`}>
                  {slide.subtitle}
                </p>
              )}
              {slide.href && slide.cta && (
                <a
                  href={slide.href}
                  className={`inline-block ${isCompact ? "mt-2 px-5 py-2.5 text-[11px]" : "mt-4 px-7 py-3.5 text-sm"} bg-white text-[#1a3a5c] font-bold rounded-lg hover:bg-neutral-100 transition-colors shadow-xl pointer-events-auto uppercase tracking-wider`}
                >
                  {slide.cta}
                </a>
              )}
            </div>
            {slide.media && (
              <div className="flex-1 min-w-0 w-full lg:max-w-md">
                <HeroEmbedMedia media={slide.media} />
              </div>
            )}
          </div>
        )}

        {/* ─ EVENTO ─ */}
        {slide.type === "evento" && (
          <div key={`evento-${current}`} className={`relative z-10 w-full h-full flex items-center px-6 sm:px-10 ${isCompact ? "max-w-5xl" : "max-w-6xl"} mx-auto`}>
            <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 items-center w-full">
              {(slide.media || slide.imageUrl) && (
                <div className="w-full lg:flex-1 lg:min-w-0 lg:max-w-xl pointer-events-auto space-y-3">
                  {/* Media embedded (video/gallery/quote/pdf/slide/document) */}
                  {slide.media && <HeroEmbedMedia media={slide.media} />}
                  {/* Thumbnail foto dell'evento — mostrata anche quando c'è il media.
                      In variant compact la thumb "grande" viene omessa per ridurre ingombro. */}
                  {slide.imageUrl && (
                    slide.media ? (
                      /* Con media: thumb piccola alla base, alignata a destra */
                      <div className="hidden sm:flex justify-end">
                        {/* Perf: thumb evento (con media) — dim fissi max 128px */}
                        <Image
                          src={slide.imageUrl}
                          alt={slide.title}
                          width={128}
                          height={171}
                          sizes={isCompact ? "(min-width: 640px) 96px, 80px" : "(min-width: 640px) 128px, 96px"}
                          className={`${isCompact ? "w-20 sm:w-24" : "w-24 sm:w-32"} h-auto rounded-lg shadow-xl ring-1 ring-white/20`}
                        />
                      </div>
                    ) : !isCompact ? (
                      /* Senza media (solo variante full): thumb grande classica */
                      <div className="hidden sm:block">
                        {/* Perf: thumb evento "grande" classica — dim fissi 208x278 (ratio ~3/4) */}
                        <Image
                          src={slide.imageUrl}
                          alt={slide.title}
                          width={208}
                          height={278}
                          sizes="(min-width: 640px) 208px, 144px"
                          className="w-36 sm:w-52 h-auto rounded-xl shadow-2xl ring-2 ring-white/20 mx-auto lg:mx-0"
                        />
                      </div>
                    ) : (
                      /* Compact senza media: thumb più contenuta */
                      <div className="hidden sm:block">
                        {/* Perf: thumb evento compatta — dim fissi 144x192 */}
                        <Image
                          src={slide.imageUrl}
                          alt={slide.title}
                          width={144}
                          height={192}
                          sizes="(min-width: 640px) 144px, 112px"
                          className="w-28 sm:w-36 h-auto rounded-xl shadow-2xl ring-2 ring-white/20 mx-auto lg:mx-0"
                        />
                      </div>
                    )
                  )}
                </div>
              )}
              <div className="flex-1 min-w-0 animate-fade-in text-center lg:text-left">
                {slide.badge && (
                  // A11y + Design-FIX:
                  // - amber-500+white falliva contrasto (1.94:1)
                  // - amber-800 passava il contrasto ma si confondeva con la category badge "cultura" (anch'essa amber-800)
                  // - bg-[#1a3a5c] (brand blue) + text-white = ~10:1, semanticamente "meta/sistema",
                  //   fuori dalla palette categorie quindi nessun conflitto
                  <span className={`inline-block ${isCompact ? "mb-2 px-2.5 py-1 text-[10px]" : "mb-4 px-3 py-1.5 text-[11px]"} bg-[#1a3a5c] text-white font-bold uppercase tracking-[0.15em] rounded-full shadow-md`}>
                    {slide.badge}
                  </span>
                )}
                <h2 className={`${isCompact ? "text-xl sm:text-2xl lg:text-3xl mb-2" : "text-2xl sm:text-4xl lg:text-5xl mb-3"} font-extrabold text-white leading-tight drop-shadow-lg tracking-tight`}>
                  {slide.title}
                </h2>
                {slide.subtitle && (
                  <p className={`text-amber-200/90 ${isCompact ? "text-xs sm:text-sm mb-3" : "text-sm sm:text-base mb-6"} font-semibold`}>
                    {slide.subtitle}
                  </p>
                )}
                {slide.href && slide.cta && (
                  <a
                    href={slide.href}
                    className={`inline-flex items-center gap-2 bg-white text-[#1a3a5c] font-bold ${isCompact ? "px-4 py-2 text-[11px]" : "px-6 py-3 text-sm"} rounded-lg hover:bg-neutral-100 transition-colors shadow-xl pointer-events-auto uppercase tracking-wider`}
                  >
                    {slide.cta}
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─ APPROFONDIMENTO: layout a due colonne con impatto ─ */}
        {slide.type === "approfondimento" && (
          <div
            key={`appro-${current}`}
            className="relative z-30 w-full max-w-6xl mx-auto px-4 sm:px-10 py-2 sm:py-4
                       flex flex-col sm:grid sm:grid-cols-[auto,1fr] gap-4 sm:gap-8 lg:gap-12 items-center
                       max-h-full overflow-hidden"
          >
            {/* Post-it card — se è disponibile un'immagine (tipicamente lo
                 screenshot catturato dal backend) la mostriamo come "polaroid"
                 dello stesso ingombro del post-it; altrimenti fallback sul
                 post-it giallo classico. */}
            {slide.imageUrl ? (
              <div
                className="postit-scene animate-postit-enter pointer-events-auto self-center mx-auto sm:mx-0 shrink-0"
                style={{ perspective: "900px" }}
              >
                <article
                  className="relative bg-white rounded-md shadow-2xl ring-1 ring-black/10 overflow-hidden"
                  style={{
                    width: "min(72vw, 300px)",
                    aspectRatio: "3 / 4",
                    transform: "rotate(-1.5deg)",
                    animation: "postit-idle 5s ease-in-out infinite",
                    transformOrigin: "50% 10%",
                  }}
                >
                  <span className="postit-pushpin" aria-hidden="true" />
                  <span className="postit-pushpin-shadow" aria-hidden="true" />
                  <div className="absolute inset-2 overflow-hidden rounded-sm bg-neutral-100">
                    {/* Perf: post-it screenshot — aspect 3/4 fisso → Image fill 300px */}
                    <Image
                      src={slide.imageUrl}
                      alt={slide.title}
                      fill
                      sizes="300px"
                      // object-top: lo screenshot ora e' un viewport mobile
                      // dell'articolo sorgente (iPhone 13, step C12) e
                      // riprende titolo/meta/hero/lede IN CIMA. Con object-cover
                      // + object-position default si crop-pa al centro e si
                      // perde proprio quella "parte alta". Ancoriamo al top.
                      className="object-cover object-top"
                    />
                    {/* Overlay badge/titolo/autori: visibile SOLO su mobile.
                      Su desktop affianchiamo post-it (pulito) e pannello
                      (con tutti i campi), mentre su mobile stacchiamo in
                      verticale mostrando tutte le info sul post-it e solo
                      la CTA nel pannello sotto — cosi' i campi non compaiono
                      mai due volte a nessuna dimensione schermo. */}
                    <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/85 via-black/40 to-transparent block sm:hidden" />
                    <div className="absolute inset-x-0 bottom-0 p-3 text-white block sm:hidden">
                      {slide.subtitle && (
                        <span
                          className="inline-block text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white shadow"
                          style={{ backgroundColor: catColors.accent }}
                        >
                          {slide.subtitle}
                        </span>
                      )}
                      <h3 className="mt-1.5 text-[0.82rem] sm:text-sm font-bold leading-snug line-clamp-3 drop-shadow-md">
                        {slide.title}
                      </h3>
                      {slide.authors && slide.authors.length > 0 && (
                        <p className="mt-1 text-[10px] text-white/85 line-clamp-1">
                          di {slide.authors.join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                </article>
              </div>
            ) : (
              <div className="postit-scene animate-postit-enter pointer-events-auto self-center mx-auto sm:mx-0 shrink-0">
                <article className="postit-card" style={postitStyle}>
                  <span className="postit-pushpin" aria-hidden="true" />
                  <span className="postit-pushpin-shadow" aria-hidden="true" />
                  <span className="postit-shine" aria-hidden="true" />
                  <span className="postit-curl" aria-hidden="true" />

                  <div className="postit-content">
                    {slide.subtitle && (
                      <span className="postit-kicker" style={{ backgroundColor: catColors.accent }}>
                        {slide.subtitle}
                      </span>
                    )}
                    <h3 className="postit-title line-clamp-5">
                      {slide.title}
                    </h3>
                    {slide.authors && slide.authors.length > 0 && (
                      <p className="postit-authors">
                        {slide.authors.join(", ")}
                      </p>
                    )}
                  </div>
                </article>
              </div>
            )}

            {/* Panel testo — contrasto alto per leggibilità */}
            <div className="hero-approfondimento-panel animate-fade-in pointer-events-auto text-center sm:text-left min-w-0">
              {slide.badge && (
                    <span className={`inline-block mb-3 text-[11px] font-bold uppercase tracking-[0.15em] px-3 py-1.5 rounded-full shadow-sm ${catColors.badge} ${catColors.badgeText}`}>
                  {slide.badge}
                </span>
              )}
              <h2 className={`hero-approfondimento-title ${isCompact ? "text-base sm:text-lg lg:text-xl" : "text-lg sm:text-2xl lg:text-3xl"} font-extrabold leading-tight mb-2 tracking-tight`}>
                {slide.title}
              </h2>
              {slide.authors && slide.authors.length > 0 && (
                <p className="hero-approfondimento-meta text-sm font-semibold mb-2 tracking-wide">
                  di {slide.authors.join(", ")}
                </p>
              )}
              {/* Excerpt: nascosto su mobile (troppo poco spazio), visibile da sm */}
              {slide.excerpt && (
                <p className="hero-approfondimento-excerpt hidden sm:block text-sm sm:text-[0.95rem] leading-relaxed mb-3 line-clamp-3 lg:line-clamp-4 max-w-prose">
                  {slide.excerpt}
                </p>
              )}
              {slide.source && (
                <p className="hero-approfondimento-source hidden sm:block text-xs mb-2 italic">
                  Fonte: {slide.source}
                </p>
              )}
              {slide.href && slide.cta && (
                <a
                  href={slide.href}
                  className="inline-flex items-center gap-2 bg-white text-[#1a3a5c] font-bold px-6 py-3 rounded-lg hover:bg-neutral-100 transition-colors shadow-xl text-sm uppercase tracking-wider mt-1"
                >
                  {slide.cta}
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Paginazione stile CMC ── */}
      {slides.length > 1 && (
        <div className="absolute bottom-4 sm:bottom-8 right-4 sm:right-10 z-30 pointer-events-auto">
          <div className="flex items-center gap-0 bg-black/30 backdrop-blur-md rounded-full border border-white/15 shadow-lg">
            <button
              onClick={prev}
              aria-label="Slide precedente"
              className="p-2.5 sm:p-3 text-white/80 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-white text-sm font-semibold tabular-nums min-w-[3ch] text-center select-none">
              {current + 1}/{slides.length}
            </span>
            <button
              onClick={next}
              aria-label="Slide successiva"
              className="p-2.5 sm:p-3 text-white/80 hover:text-white transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Barra progresso */}
      {!paused && (
        <div
          key={`${current}-bar`}
          className="absolute bottom-0 left-0 h-1 bg-white/50 z-20"
          style={{ animation: `progress-bar ${AUTOPLAY_MS}ms linear forwards` }}
        />
      )}
    </section>
  );
}
